import { AnimatePresence, useInView, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { CameraIcon, ChevronLeft, ChevronRight } from "lucide-react";

import React, { useEffect, useRef } from "react";
import { Icon360, IconX } from "@tabler/icons-react";

export default function CarouselRosti({ className, images, link }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [direction, setDirection] = React.useState("right");
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [oguloLink, setOguloLink] = React.useState(null);
  const thumbnailRefs = useRef([]);
  const containerRef = useRef(null);

  useEffect(()=>{
    for (const li in link){
      if(link[li].url.includes("ogulo")){
        setOguloLink(link[li])
  }
}
 },[])

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault(); 
    setDirection("right");

    setCurrentIndex((prev) => (prev + 1 === images.length ? 0 : prev + 1));
  };
  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setDirection("left");

    setCurrentIndex((prev) => (prev - 1 < 0 ? images.length - 1 : prev - 1));
  };

  const handleDotClick = (e, index) => {
    e.stopPropagation();
    // e.preventDefault(); // optional if needed
    setDirection(index > currentIndex ? "right" : "left");
    setCurrentIndex(index);
  };

  const handleOnClick = (e) => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const container = containerRef.current;
    const currentThumbnail = thumbnailRefs.current[currentIndex];

    if (container && currentThumbnail) {
      // Temporarily disable snapping
      container.style.scrollSnapType = "none";

      currentThumbnail.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });

      // Re-enable snapping after scroll completes
      setTimeout(() => {
        container.style.scrollSnapType = "";
      }, 500); // match your scroll duration
    }
  }, [currentIndex]);

  const variants = {
    hiddenRight: { x: "100%", opacity: 0.5, scale: 0.95 },
    hiddenLeft: { x: "-100%", opacity: 0.5, scale: 0.95 },
    visible: { x: 0, opacity: 1, scale: 1 },
    exitRight: { x: "100%", opacity: 0.5, scale: 0.95 },
    exitLeft: { x: "-100%", opacity: 0.5, scale: 0.95 },
  };

  const minimapVariants = {
    active: {
      scale: 1.03,
      opacity: 0.8,
    },
    inactive: {
      scale: 0.95,
      opacity: 0.3,
    },
    hover: {
      scale: 1,
      opacity: 0.5,
    },
  };
  const fullscreenVariants = {
    open: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    close: {
      opacity: 0,
      scale: 0.5,
      transition: { duration: 0.3, ease: "easeInOut", origin: "center" },
    },
  };
  return (
    <div>
      {isFullscreen && (
        <AnimatePresence mode="wait">
          <motion.div
            initial="close"
            animate="open"
            variants={fullscreenVariants}
            className="fixed inset-0 bg-black flex justify-center items-center z-50"
          >
            <div className="p-4  relative">
              <div className="absolute bottom-3 left-0 w-full md:h-[5rem] h-[3rem] flex text-base md:text-lg  justify-center items-center text-white bg-black opacity-50 z-40">
                {images[currentIndex]?.title}
              </div>
              <motion.img
                loading="lazy"
                variants={variants}
                animate="visible"
                initial={direction === "right" ? "hiddenRight" : "hiddenLeft"}
                exit={direction === "right" ? "exitLeft" : "exitRight"}
              src={images[currentIndex].big_url}
                className="max-w-full  max-h-full md:max-h-[100vh] object-contain"
              />
            </div>
            <button
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/80 text-white rounded-full p-2"
              onClick={handleOnClick}
              aria-label="Close fullscreen"
            >
              <IconX className="w-6 h-6" />
            </button>
            <button
              className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-3 shadow-md"
              onClick={handlePrev}
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white bg-opacity-75 rounded-full p-3 shadow-md"
              onClick={handleNext}
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </motion.div>
        </AnimatePresence>
      )}

      <div
        id="Carousel"
        onClick={handleOnClick}
        className=" flex flex-col md:flex-row gap-5 mx-auto "
      >
        <div
          className={cn(
            "relative w-full h-[60vw] sm:h-[30rem] md:h-[30rem] lg:h-[30rem] overflow-hidden rounded-lg",
            className
          )}
        >
          <AnimatePresence mode="popLayout">
            <motion.img
              className="w-[98%] cursor-pointer aspect-[16/9]  bg-bottom sm:aspect-[4/3] md:aspect-[3/2] mx-auto md:w-full h-full  rounded-lg object-cover"
              src={images[currentIndex].big_url}
              key={currentIndex}
              animate="visible"
              initial={direction === "right" ? "hiddenRight" : "hiddenLeft"}
              exit={direction === "right" ? "exitLeft" : "exitRight"}
              variants={variants}
              transition={{ type: "tween", duration: 0.4, ease: "easeInOut" }}
            />
          </AnimatePresence>
          <div className="w-fit absolute right-3 bottom-3 py-2 flex justify-between px-2 gap-2">
            <div className="bg-white/50 text-gray-500 font-medium text-sm md:text-base rounded-lg p-2">
              <CameraIcon className="inline" /> {currentIndex + 1} /{" "}
              {images.length}
            </div>
          </div>
          {oguloLink && (
            <div className="w-fit absolute left-3 bottom-3 py-2 flex justify-between px-2 gap-2">
              <a target="__blanc" href={oguloLink.url}>
                <div className="bg-white/90 hover:bg-white/50 translate-all duration-700 ease-out cursor-pointer text-gray-500 font-medium text-xs md:text-sm rounded-lg p-2">
                  <Icon360 className="inline" /> 360° Rundgang
                </div>
              </a>
            </div>
          )}

          <div className="w-full absolute right-0 top-[45%] py-2 flex justify-between px-2 gap-2">
            <button
              className="p-3 bg-prime-100/50 transition-all hover:bg-prime-100/100 rounded-full "
              onClick={(e) => { e.stopPropagation(); handlePrev(e); }}
            >
              <ChevronLeft />
            </button>
            <button
              className="p-3 bg-prime-100/50 transition-all hover:bg-prime-100/100 rounded-full  "
              onClick={(e) => { e.stopPropagation(); handleNext(e); }}
            >
              <ChevronRight />
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="md:flex-col flex flex-row px-2    overflow-x-auto md:overflow-y-auto   md:h-[30rem] lg:h-[30rem]  snap-mandatory scroll-smooth scrollbar-thin"
        >
          <div className="w-[calc(10%-5rem)] shrink-0" />
          {images.map((image, index) => (
            <motion.img
              ref={(el) => (thumbnailRefs.current[index] = el)}
              key={index}
              src={image.big_url}
              onClick={(e) => { e.stopPropagation(); handleDotClick(e, index); }}
              variants={minimapVariants}
              initial="inactive"
              animate={index === currentIndex ? "active" : "inactive"}
              whileHover={index !== currentIndex ? "hover" : "active"}
              className={`md:w-[15rem] w-[8rem] my-1 snap-center aspect-video object-cover rounded-lg cursor-pointer ${
                currentIndex === index && "border-prime-800 "
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
